import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { trace } from './traceLog';

/**
 * Wake-time probes for the stale-connection investigation (2026-09-23).
 * Recorded at native resume and at web visibility-return, per the approved
 * diagnostic scope: exactly two probes here (a one-document Firestore read
 * and an auth getIdToken timing) plus the reconnect cycle, which traces
 * itself in firestoreReconnect.js.
 *
 * The Firestore probe reads the signed-in user's OWN users/{uid} doc: the
 * dedicated read-only health document the design called for needs a rules
 * change plus a production deploy, so until that ships we use the one
 * document existing rules guarantee the caller can read, and we report
 * permission-denied as its own outcome, distinct from timeout, so a rules
 * surprise can't be mistaken for a transport hang. Only outcomes, codes and
 * durations are traced; never document contents.
 *
 * Probes are fire-and-forget: nothing awaits them, they can never block
 * navigation, rendering, or each other. A probe that never settles shows up
 * in the trace as a start with a timeout end.
 */

const PROBE_TIMEOUT_MS = 8000;

function withProbeTimeout(promise) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ outcome: 'timeout' }), PROBE_TIMEOUT_MS);
    promise.then(
      (value) => { clearTimeout(timer); resolve({ outcome: 'ok', value }); },
      (err) => {
        clearTimeout(timer);
        const code = err?.code || err?.name || 'unknown';
        resolve({ outcome: code === 'permission-denied' ? 'permission-denied' : `error:${code}` });
      }
    );
  });
}

export function probeFirestoreRead(tag) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    trace('probe:fs:skip', { tag, reason: 'no_user' });
    return;
  }
  const startedAt = Date.now();
  trace('probe:fs:start', { tag });
  withProbeTimeout(getDoc(doc(db, 'users', uid))).then((r) => {
    trace('probe:fs:end', { tag, outcome: r.outcome, ms: Date.now() - startedAt });
  });
}

export function probeAuthToken(tag) {
  const user = auth.currentUser;
  if (!user) {
    trace('probe:auth:skip', { tag, reason: 'no_user' });
    return;
  }
  const startedAt = Date.now();
  trace('probe:auth:start', { tag });
  withProbeTimeout(user.getIdToken()).then((r) => {
    // Outcome and elapsed only. The token itself is never touched or logged.
    trace('probe:auth:end', { tag, outcome: r.outcome, ms: Date.now() - startedAt });
  });
}
